// 材料QR番号の検索クエリパーサー。
// 検索文字列(`7` 単体 / `1-50` 範囲 / `51+10` 個数指定 の `,` 区切り)を、
// id を全展開せず「範囲(IdRange)の配列」へ正規化する。巨大範囲(例 1-1000000)でも
// ペイロード・メモリが肥大しないよう、以降の処理(API送信・IndexedDB絞り込み)も範囲のまま扱う。
//
// 印刷QRのペイロード形式(QrListDialog.tsx参照): `okumura-traceability:<version>:<payload>`
// (payload は `123` 単体 または `123+5` の個数指定)。スキャナはこの生文字列、または
// それを要素とするJSON配列文字列(例 '["okumura-traceability:1:123","124+3"]')を返す。
// パーサへ渡す前に、まずプレフィックスを剥がして payload だけを取り出す。

// 材料QR番号の閉区間(start ≤ end)。単体指定は start === end。
export type IdRange = { start: number; end: number };

const QR_PREFIX_PATTERN = /^okumura-traceability:\d+:/;

function stripQrPrefix(token: string): string {
  return token.trim().replace(QR_PREFIX_PATTERN, "");
}

// スキャン生文字列からトークン配列を取り出す。JSON配列ならその要素、そうでなければ`,`区切り。
function extractTokens(material: string): string[] {
  const trimmed = material.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((value) => typeof value === "string" || typeof value === "number")
          .map((value) => String(value));
      }
    } catch {
      // JSONとして壊れている場合はカンマ区切りにフォールバックする
    }
  }
  return trimmed.split(",");
}

// 1トークンを範囲へ変換する。無効なトークンは null。
function rangeOfToken(token: string): IdRange | null {
  // 個数指定(`123+5` = 123〜127)。個数0は選択なし扱い。
  if (/^\d+\+\d+$/.test(token)) {
    const splited = token.split("+");
    const headId = Number(splited[0]);
    const count = Number(splited[1]);
    if (Number.isInteger(headId) && Number.isInteger(count) && headId > 0 && count > 0) {
      return { start: headId, end: headId + count - 1 };
    }
    return null;
  }
  // 範囲指定(`1-50`)
  if (/^\d+\-\d+$/.test(token)) {
    const splited = token.split("-");
    const headId = Number(splited[0]);
    const tailId = Number(splited[1]);
    if (Number.isInteger(headId) && Number.isInteger(tailId) && headId > 0 && tailId >= headId) {
      return { start: headId, end: tailId };
    }
    return null;
  }
  // 単体指定
  if (/^\d+$/.test(token)) {
    const id = Number(token);
    if (id > 0) {
      return { start: id, end: id };
    }
  }
  return null;
}

// 範囲配列を正規化する: 開始昇順に並べ、重複・隣接する範囲は結合する。
function normalizeRanges(ranges: IdRange[]): IdRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: IdRange[] = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last !== undefined && range.start <= last.end + 1) {
      last.end = Math.max(last.end, range.end);
      continue;
    }
    merged.push({ ...range });
  }
  return merged;
}

// 検索文字列(またはスキャン生文字列)を正規化済みの範囲配列へパースする。
export function ParseMaterial(material: string): IdRange[] {
  const tokens = extractTokens(material)
    .map((token) => stripQrPrefix(token).replace(/[^0-9+\-]/g, ""))
    .filter((value) => value !== "");
  const ranges: IdRange[] = [];
  for (const token of tokens) {
    const range = rangeOfToken(token);
    if (range !== null) {
      ranges.push(range);
    }
  }
  return normalizeRanges(ranges);
}

// 明示的な id 列(スキャン済QRの一覧等)を圧縮した範囲配列へ変換する。
export function rangesFromIds(ids: number[]): IdRange[] {
  return normalizeRanges(ids.map((id) => ({ start: id, end: id })));
}

// 範囲配列を検索文字列表記(`1-50,60`)へ変換する。URL・検索入力欄との相互変換に使う。
export function rangesToString(ranges: IdRange[]): string {
  return ranges
    .map((range) => {
      if (range.start === range.end) {
        return `${range.start}`;
      }
      return `${range.start}-${range.end}`;
    })
    .join(",");
}

// 範囲配列が含む id の総数(展開せずに数える)。
export function idCountOfRanges(ranges: IdRange[]): number {
  return ranges.reduce((sum, range) => sum + (range.end - range.start + 1), 0);
}

// id が範囲配列に含まれるか(展開せずに判定)。
export function isIdInRanges(id: number, ranges: IdRange[]): boolean {
  return ranges.some((range) => id >= range.start && id <= range.end);
}

// 範囲配列を id 列へ展開する。件数が物理的に限られる用途(スキャン済QRのスロット等)専用。
// 検索ペイロードやIndexedDB絞り込みには使わないこと(巨大範囲で肥大するため)。
export function idsFromRanges(ranges: IdRange[]): number[] {
  const ids: number[] = [];
  for (const range of ranges) {
    for (let id = range.start; id <= range.end; id++) {
      ids.push(id);
    }
  }
  return ids;
}

// 材料検索ページへの絞り込みURL文字列(searchParams=範囲圧縮済みID)を生成する。
export function materialSearchUrl(ids: number[]): string {
  return `/console/search-materials/?searchParams=${MaterialRangeCompressor(ids)}`;
}

// id 列 → 範囲圧縮した検索文字列(従来互換のヘルパー)。
export function MaterialRangeCompressor(nums: number[]): string {
  return rangesToString(rangesFromIds(nums));
}
